import { useTableViewHelpers } from '@design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import { DynamicCountryFilter } from '../../dynamic-filter-inputs/dynamic-country-filter';
import { DynamicDateFilter } from '../../dynamic-filter-inputs/dynamic-date-filter';
import { DynamicSimpleSelectFilter } from '../../dynamic-filter-inputs/dynamic-simple-select-filter';
import { DynamicTextFilter } from '../../dynamic-filter-inputs/dynamic-text-filter';
import { DynamicPhoneNumberFilter } from '../../dynamic-filter-inputs/dynamic-phone-number-filter';
import { FIELD_TYPES } from '@shared/types/field';

import customFieldFilterApi from '@shared/features/views/services/custom-field-filter-api';

import type { ReactNode } from 'react';
import type { TCustomFieldFilter } from '@shared/types/custom-field-filter';
import type { TCustomField } from '@shared/types/custom-field';

export type TDynamicFilterProps = {
  dynamicField?: TCustomField;
  values: string[];
  addOrEditDynamicFilter: (values: string[]) => void;
  onChange: (values: string[]) => void;
  options?: {
    label: string;
    value: string;
    [key: string]: string;
  }[];
};

type MapperType = Record<string, (props: TDynamicFilterProps) => ReactNode>;

const mapper: MapperType = {
  [FIELD_TYPES.shortAnswer]: DynamicTextFilter,
  [FIELD_TYPES.email]: DynamicTextFilter,
  [FIELD_TYPES.number]: DynamicTextFilter,
  [FIELD_TYPES.link]: DynamicTextFilter,
  [FIELD_TYPES.paragraph]: DynamicTextFilter,
  [FIELD_TYPES.date]: DynamicDateFilter,
  [FIELD_TYPES.country]: DynamicCountryFilter,
  [FIELD_TYPES.countryMultiselect]: DynamicCountryFilter,
  [FIELD_TYPES.dropdown]: DynamicSimpleSelectFilter,
  [FIELD_TYPES.checklist]: DynamicSimpleSelectFilter,
  [FIELD_TYPES.ratio]: DynamicSimpleSelectFilter,
  [FIELD_TYPES.phoneNumber]: DynamicPhoneNumberFilter,
};

export const DynamicCustomFieldFilter = ({
  dynamicField,
  index,
}: {
  dynamicField: TCustomFieldFilter;
  index: number;
}) => {
  const Field =
    mapper[dynamicField.custom_field?.type as keyof typeof mapper] ||
    DefaultFilter;
  const { mutate: editCustomFieldFilter } = customFieldFilterApi.useUpdate();
  const { mutate: createCustomFieldFilter } = customFieldFilterApi.useCreate();
  const { updateCustomFieldFilter } = useTableViewHelpers();

  const addOrEditDynamicFilter = (values: string[]) => {
    if (dynamicField?.id) {
      editCustomFieldFilter(
        {
          id: dynamicField.id,
          operator: dynamicField.operator,
          type: dynamicField.custom_field?.type,
          custom_field: dynamicField.custom_field?.id,
          value: values,
        },
        {
          onSuccess: (data) => updateCustomFieldFilter(data, index),
        }
      );
    } else {
      createCustomFieldFilter(
        {
          operator: dynamicField.operator,
          type: dynamicField.custom_field!.type,
          custom_field: dynamicField.custom_field!.id,
          value: values,
        },
        {
          onSuccess: (data) => updateCustomFieldFilter(data, index),
        }
      );
    }
  };

  return (
    <Field
      dynamicField={dynamicField.custom_field}
      values={dynamicField.value || []}
      addOrEditDynamicFilter={addOrEditDynamicFilter}
      onChange={(values: string[]) =>
        updateCustomFieldFilter({ value: values }, index)
      }
      options={
        dynamicField.custom_field?.options?.map((opt) => ({
          label: opt,
          value: opt,
        })) || []
      }
    />
  );
};

const DefaultFilter = () => {
  return <div>No Filter Found</div>;
};
